import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink, Maximize2 } from 'lucide-react';

interface HTMLViewerModalProps {
  open:    boolean;
  url:     string | null;
  title:   string;
  onClose: () => void;
}

/**
 * Modal full-screen para abrir una guía HTML interactiva dentro de un iframe.
 *
 * Las guías viven en Supabase Storage (bucket `guides-html`) y cada una tiene
 * su propio `index.html` + assets. El iframe carga la URL pública del index
 * y queda autocontenido.
 *
 * Cerrar: botón X arriba a la derecha o tecla Escape.
 * Bonus: botón "Abrir en nueva pestaña" por si el usuario quiere
 * pantalla completa de browser.
 *
 * Por qué createPortal: igual que PDFViewerModal — algunos contenedores padre
 * tienen `transform` (`animate-slide-up` etc.) que convierten el fixed en
 * relativo. Renderizar en document.body bypasea ese problema.
 */
export function HTMLViewerModal({ open, url, title, onClose }: HTMLViewerModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Cerrar con Escape + bloquear scroll del fondo mientras está abierto.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !url) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Guía: ${title}`}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
    >
      {/* Header / toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08] bg-cubus-deep/80 backdrop-blur flex-shrink-0">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[2px] uppercase font-semibold text-cubus-lime mb-0.5">
            Guía Interactiva
          </div>
          <h2 className="text-sm font-medium text-white truncate max-w-[60vw]">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 hover:border-cubus-lime/40 text-white/80 transition-all"
            title="Abrir en nueva pestaña"
            aria-label="Abrir en nueva pestaña"
          >
            <Maximize2 className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">Nueva pestaña</span>
          </a>
          <button
            onClick={onClose}
            className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 hover:border-cubus-lime/40 text-white/80 transition-all focus-lime"
            title="Cerrar (Esc)"
            aria-label="Cerrar"
          >
            <span className="text-sm">Cerrar</span>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Iframe con la guía */}
      <iframe
        ref={iframeRef}
        src={url}
        title={title}
        // tabIndex permite que el iframe reciba foco; sin esto las flechas
        // del teclado no llegan al HTML interactivo hasta que el usuario
        // hace click adentro.
        tabIndex={0}
        onLoad={() => {
          // Cuando termina de cargar, focuseamos el iframe para que los
          // eventos de teclado (flechas para navegar pasos de la guía)
          // funcionen sin que el usuario tenga que clickear primero.
          // Probamos contentWindow.focus() primero (más confiable cuando
          // same-origin), si falla por cross-origin caemos al iframe element.
          try {
            iframeRef.current?.contentWindow?.focus();
          } catch {
            iframeRef.current?.focus();
          }
        }}
        className="flex-1 w-full border-0 block bg-white"
        // allow-scripts: las guías usan JS para sus controles interactivos
        // allow-same-origin: para que los recursos relativos resuelvan
        // allow-popups: por si alguna guía abre links (ej. videos externos)
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
    </div>,
    document.body,
  );
}
